const {
  MarkerInfoKey,
  REFERENCE_MARKER_DELTA_INDICATOR,
  ReferenceSearchType,
} = require("../enums/MarkerInfoKey");
const TemperatureUnit = require("../enums/TemperatureUnit");
const {
  generateTemperatureObject,
  generateDeltaObject,
} = require("./ObjectGenerators");

/**
 * Generate marker info
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {Object} infoJSON info.json file contents from .is2 file
 * @returns {Array} List of marker info
 */
const generateMarkerInfo = (renderer, infoJSON) => {
  const markerInfoList = [];

  if (!renderer) {
    throw new Error("IRRenderer instance is required");
  }

  const renderedOutputJSON = renderer.irrenderer_rendering_get_output_data();

  const markers = renderedOutputJSON?.OUTPUT_MARKERS;

  // Reference markers in the past were determined by marker name
  const referenceMarkerName =
    infoJSON?.IRVIEWER?.referenceTemperature?.markerName;
  // Marker ID is how the reference marker is determined as of 12/8/2023
  const referenceMarkerId = infoJSON?.IRVIEWER?.referenceMarkerId;

  const {
    marker: referenceMarker,
    indicator: referenceMarkerIndicator,
    searchType: referenceSearchType,
  } = findReferenceMarker(markers, referenceMarkerId, referenceMarkerName);
  let referenceMarkerInfo = null;
  if (referenceMarker) {
    // Go ahead and generate the reference marker info
    // to be used for calculating deltas for other markers

    const basicReferenceMarkerInfo =
      generateBasicMarkerInfoObject(referenceMarker);

    if (basicReferenceMarkerInfo != null) {
      referenceMarkerInfo = {
        ...basicReferenceMarkerInfo,
        [MarkerInfoKey.DELTA]: REFERENCE_MARKER_DELTA_INDICATOR,
        [MarkerInfoKey.REFERENCE]: true,
      };
    }
  }

  // Add Standard Markers
  Object.keys(markers.STANDARD).forEach((markerId) => {
    const markerInfo = createMarkerInfo(
      markerId,
      markers.STANDARD,
      referenceMarkerInfo,
      referenceMarkerIndicator,
      referenceSearchType
    );

    if (markerInfo) {
      markerInfoList.push(markerInfo);
    }
  });

  // Add User Defined Markers
  Object.keys(markers.USERDEFINED).forEach((markerGroup) => {
    // These "MarkerGroups" are the types of markers (e.g., Ellipses, Points, etc.)
    const currentMarkerGroup = markers.USERDEFINED[markerGroup];
    Object.keys(currentMarkerGroup).forEach((markerId) => {
      const markerInfo = createMarkerInfo(
        markerId,
        currentMarkerGroup,
        referenceMarkerInfo,
        referenceMarkerIndicator,
        referenceSearchType
      );

      if (markerInfo) {
        markerInfoList.push(markerInfo);
      }
    });
  });

  return markerInfoList;
};

/**
 * Retrieves the marker temperature from the given marker argument.
 *
 * @param {Object} markerArg - The marker argument containing characteristics.
 * @returns {number} The marker temperature in Celsius.
 */
const getMarkerTemp = (markerArg) => {
  return (
    markerArg.characteristics.markerTempC_Max ??
    markerArg.characteristics.markerTempC_Average
  );
};

/**
 * Appends marker information to the markerInfoList.
 *
 * @param {string} markerId - The marker ID.
 * @param {Object} parentObject - The parent object containing the marker.
 * @param {Object} referenceMarkerInfo - The reference marker info.
 * @param {string} referenceMarkerIndicator - The reference marker indicator.
 * @param {ReferenceSearchType} referenceSearchType - The reference search type.
 */
const createMarkerInfo = (
  markerId,
  parentObject,
  referenceMarkerInfo,
  referenceMarkerIndicator,
  referenceSearchType
) => {
  const currentMarker = parentObject[markerId];

  // Skip markers that are not enabled (i.e., not visible in the image)
  if (!currentMarker?.characteristics?.markerEnabledFlag) {
    return null;
  }

  let isReferenceMarker = false;

  switch (referenceSearchType) {
    case ReferenceSearchType.REFERENCE_ID:
      isReferenceMarker = referenceMarkerIndicator === markerId;
      break;
    case ReferenceSearchType.REFERENCE_NAME:
      isReferenceMarker =
        referenceMarkerIndicator === currentMarker.characteristics.markerName;
      break;
    case ReferenceSearchType.NONE:
    default:
      break;
  }

  if (isReferenceMarker) {
    // Return the reference marker info
    // Doing so here to keep the marker list in order
    return referenceMarkerInfo;
  }

  let currentMarkerInfo = generateBasicMarkerInfoObject(currentMarker);

  if (currentMarkerInfo == null) {
    return null;
  }

  if (referenceMarkerInfo) {
    const referenceCelsius =
      referenceMarkerInfo[MarkerInfoKey.MAX_TEMPERATURE][
        TemperatureUnit.CELSIUS
      ];
    currentMarkerInfo[MarkerInfoKey.DELTA] = generateDeltaObject(
      currentMarkerInfo[MarkerInfoKey.MAX_TEMPERATURE][TemperatureUnit.CELSIUS],
      referenceCelsius
    );
    currentMarkerInfo[MarkerInfoKey.REFERENCE] = false;
  }

  return currentMarkerInfo;
};

/**
 * Finds the reference marker in the markers object.
 *
 * @param {Object} markers - The markers object.
 * @param {string} referenceMarkerId - The id of the reference marker.
 * @param {string} referenceMarkerName - The name of the reference marker (for backwards compatibility)
 * @returns {Object|null} object containing the reference marker and the search type
 * @property {Object} marker - The reference marker object.
 * @property {string} indicator - The reference marker indicator.
 * @property {ReferenceSearchType} searchType - The search type used to find the reference marker.
 */
const findReferenceMarker = (
  markers,
  referenceMarkerId,
  referenceMarkerName = null
) => {
  let referenceMarkerFoundViaName = null;
  let referenceMarkerFoundViaId = null;

  const defaultReturnObject = {
    marker: null,
    indicator: null,
    searchType: ReferenceSearchType.NONE,
  };

  if (!referenceMarkerId && !referenceMarkerName) {
    return defaultReturnObject;
  }

  const testForReferenceMarker = (markerId, parentObject) => {
    const currentMarker = parentObject[markerId];

    // Check if the current marker is the reference marker based on ID
    if (referenceMarkerId && markerId === referenceMarkerId) {
      referenceMarkerFoundViaId = currentMarker;
    }

    // Check if the current marker is the reference marker based on name
    if (
      referenceMarkerName &&
      currentMarker.characteristics.markerName === referenceMarkerName
    ) {
      referenceMarkerFoundViaName = currentMarker;
    }
  };
  // Search Standard Markers
  Object.keys(markers.STANDARD).forEach((markerId) => {
    testForReferenceMarker(markerId, markers.STANDARD);
  });

  // Search User Defined Markers
  Object.keys(markers.USERDEFINED).forEach((markerGroup) => {
    // Unlike standard markers, user defined markers are grouped by type
    // (e.g., Ellipses, Points, etc.)
    const currentMarkerGroup = markers.USERDEFINED[markerGroup];
    Object.keys(currentMarkerGroup).forEach((marker) => {
      testForReferenceMarker(marker, currentMarkerGroup);
    });
  });

  // Prefer the reference marker found by ID
  if (referenceMarkerFoundViaId) {
    return {
      marker: referenceMarkerFoundViaId,
      indicator: referenceMarkerId,
      searchType: ReferenceSearchType.REFERENCE_ID,
    };
  } else if (referenceMarkerFoundViaName) {
    return {
      marker: referenceMarkerFoundViaName,
      indicator: referenceMarkerName,
      searchType: ReferenceSearchType.REFERENCE_NAME,
    };
  } else {
    return defaultReturnObject;
  }
};

/**
 * Generates a basic marker info object.
 *
 * @param {Object} markerObject - The marker object.
 * @returns {Object} The basic marker info object.
 * @property {string} MARKER_NAME - The marker name.
 * @property {Object} MAX_TEMPERATURE - The maximum temperature object.
 */
const generateBasicMarkerInfoObject = (markerObject) => {
  const markerName = markerObject.characteristics.markerName;
  const maxTemperatureCelsius = getMarkerTemp(markerObject);

  if (maxTemperatureCelsius == null) {
    console.warn(
      `No temperature value found for marker ${markerName}. Skipping.`
    );

    return null;
  }

  let maxTemperatureObject = {};
  try {
    const maxTemperatureCelsiusFloat = Number.parseFloat(maxTemperatureCelsius);
    if (isNaN(maxTemperatureCelsiusFloat)) {
      throw new Error(`Invalid temperature value: ${maxTemperatureCelsius}`);
    }
    maxTemperatureObject = generateTemperatureObject(maxTemperatureCelsius);
  } catch (e) {
    console.error("Error generating temperature object for marker", markerName);
    throw e;
  }

  referenceMarkerInfo = {
    [MarkerInfoKey.MARKER_NAME]: markerName,
    [MarkerInfoKey.MAX_TEMPERATURE]: maxTemperatureObject,
  };

  return referenceMarkerInfo;
};

module.exports = {
  generateMarkerInfo,
};
